import { ErrorResponse } from '@netprotections/berlin-bff';
import { createChannel, arrayToEnumObject } from '@berlin-front/common/utils';

export const API_ERROR = 'API_ERROR';
export const API_ERROR_TYPE = arrayToEnumObject([
  'SYSTEM_ERROR',
  'SHOP_ERROR',
  'MAINTAINANCE_ERROR',
  'BUSINESS_ERROR',
] as const);

export type AtoneApiErrorType = (typeof API_ERROR_TYPE)[keyof typeof API_ERROR_TYPE];
export type AtoneApiErrorMessagePayload = {
  service?: {
    service_type: string;
    modal_mode?: string;
  };
  errorType: AtoneApiErrorType;
  errorResponse?: ErrorResponse;
  statusCode?: number;
  url: string;
  name?: string;
  message?: string;
};

export type GlobalApiErrorMessage = {
  type: typeof API_ERROR;
  payload: AtoneApiErrorMessagePayload;
};

export type BroadcastMessage =
  | {
      type: typeof API_ERROR;
      payload: AtoneApiErrorMessagePayload;
    }
  | {
      type: 'LOGOUT';
      payload: {
        serviceType?: string;
      };
    }
  | {
      type: 'JS_ERROR';
      payload: {
        service?: {
          service_type: string;
          modal_mode?: string;
        };
        name: string;
        message: string;
        stack?: string;
      };
    }
  | {
      type: 'MODAL_CONTROL_CANCELLED';
      payload: {
        serviceType?: string;
      };
    }
  | {
      type: 'MODAL_CONTROL_SUCCEEDED';
      payload: {
        serviceType: string;
      };
    }
  | {
      type: 'MODAL_CONTROL_FAILED';
      payload: {
        serviceType?: string;
        noResult?: true;
      };
    }
  | {
      type: 'MODAL_CONTROL_INVALID';
    }
  // TODO: 以下はサービスに強くに紐づくメッセージなのでこれからは切り離したい。
  | {
      type: 'PRE_PAYMENT_REQUEST';
    }
  | {
      type: 'PRE_PAYMENT_RESPONSE';
      payload:
        | {
            /** 呼び出し成功したかどうか */
            success: true;
            /** 結果 */
            data: boolean;
          }
        | { success: false };
    };

type BroadcastMessagePayload<T extends BroadcastMessage['type']> = BroadcastMessage extends infer U
  ? U extends { type: T }
    ? U extends { payload: unknown }
      ? U['payload']
      : never
    : never
  : never;

let privateChannel: MessageChannel | undefined;

const getPrivateChannel = () => {
  if (privateChannel === undefined) {
    privateChannel = createChannel();
    privateChannel.port2.start();
    privateChannel.port1.start();
    const close1 = privateChannel.port1.close.bind(privateChannel.port1);
    const close2 = privateChannel.port2.close.bind(privateChannel.port2);
    const closeChannels = () => {
      if (privateChannel) {
        close1();
        close2();
        privateChannel = undefined;
      }
    };
    privateChannel.port1.close = closeChannels;
    privateChannel.port2.close = closeChannels;
  }
  return privateChannel;
};

/**
 * postMessageによるメッセージ転送。
 * モーダル内部での通信はwindow.postMessageを使わない。
 */
export const broadcast = {
  send: (message: BroadcastMessage) => {
    getPrivateChannel().port1.postMessage(message);
  },
  listen: <T extends BroadcastMessage['type']>(
    type: T,
    listener: (payload: BroadcastMessagePayload<T>) => void,
  ): (() => void) => {
    const wrappedListener = (ev: MessageEvent<BroadcastMessage>) => {
      if (type === ev.data.type) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const args = 'payload' in ev.data ? [ev.data.payload as unknown as any] : [];
        listener(args[0]);
      }
    };
    const channel = getPrivateChannel();
    channel.port2.addEventListener('message', wrappedListener);
    return () => channel.port2.removeEventListener('message', wrappedListener);
  },
} as const;
